import React from 'react';
import { Form, Field } from 'react-final-form';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import css from './Step1Company.module.css';

const COUNTRIES = [
  'United States', 'United Kingdom', 'Germany', 'France', 'Canada',
  'Australia', 'Japan', 'South Korea', 'India', 'Brazil',
  'Mexico', 'Netherlands', 'Italy', 'Spain', 'Singapore',
  'United Arab Emirates', 'Saudi Arabia', 'Turkey', 'Indonesia', 'China',
  'Other',
];

const CURRENCIES = [
  { value: 'USD', label: 'USD – US Dollar' },
  { value: 'EUR', label: 'EUR – Euro' },
  { value: 'GBP', label: 'GBP – British Pound' },
  { value: 'AUD', label: 'AUD – Australian Dollar' },
  { value: 'CAD', label: 'CAD – Canadian Dollar' },
  { value: 'JPY', label: 'JPY – Japanese Yen' },
  { value: 'CNY', label: 'CNY – Chinese Yuan' },
  { value: 'AED', label: 'AED – UAE Dirham' },
  { value: 'SGD', label: 'SGD – Singapore Dollar' },
  { value: 'INR', label: 'INR – Indian Rupee' },
];

const BuyerStep1Business = ({ nextStep, initialValues }) => {
  const validate = values => {
    const errors = {};
    if (!values.companyName?.trim()) errors.companyName = 'Company name is required';
    if (!values.country) errors.country = 'Please select a country';
    if (!values.preferredCurrency) errors.preferredCurrency = 'Please select a preferred currency';
    return errors;
  };

  return (
    <Form
      onSubmit={nextStep}
      validate={validate}
      initialValues={initialValues}
      render={({ handleSubmit }) => (
        <form onSubmit={handleSubmit} className={css.form}>

          <div className={css.header}>
            <span className={css.step}>Step 1 of 5</span>
            <h2 className={css.title}>Business Information</h2>
            <p className={css.description}>
              Tell us about your company so we can verify your business account.
            </p>
          </div>

          <hr className={css.divider} />

          {/* ── Company Details ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Company Details</h3>

            <div className={css.fieldFull} style={{ marginBottom: 18 }}>
              <label className={css.label}>
                Company Name <span className={css.required}>*</span>
              </label>
              <Field name="companyName">
                {({ input, meta }) => (
                  <>
                    <input
                      {...input}
                      type="text"
                      className={classNames(css.input, { [css.inputError]: meta.touched && meta.error })}
                      placeholder="Your company or business name"
                    />
                    {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                  </>
                )}
              </Field>
            </div>

            <div className={css.flex}>
              <div className={css.field}>
                <label className={css.label}>
                  Country <span className={css.required}>*</span>
                </label>
                <Field name="country">
                  {({ input, meta }) => (
                    <>
                      <select
                        {...input}
                        className={classNames(css.select, { [css.inputError]: meta.touched && meta.error })}
                      >
                        <option value="">Select Country</option>
                        {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                      </select>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>

              <div className={css.field}>
                <label className={css.label}>
                  Preferred Currency <span className={css.required}>*</span>
                </label>
                <Field name="preferredCurrency">
                  {({ input, meta }) => (
                    <>
                      <select
                        {...input}
                        className={classNames(css.select, { [css.inputError]: meta.touched && meta.error })}
                      >
                        <option value="">Select Currency</option>
                        {CURRENCIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                      </select>
                      {meta.touched && meta.error && <span className={css.error}>{meta.error}</span>}
                    </>
                  )}
                </Field>
              </div>
            </div>

            <div className={css.fieldFull} style={{ marginTop: 4 }}>
              <label className={css.label}>
                Tax / VAT Number <span style={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</span>
              </label>
              <Field name="taxNumber">
                {({ input }) => (
                  <input
                    {...input}
                    type="text"
                    className={css.input}
                    placeholder="e.g. US123456789"
                  />
                )}
              </Field>
            </div>
          </section>

          {/* ── Import / Export ── */}
          <section className={css.section}>
            <h3 className={css.sectionTitle}>Import / Export Details</h3>
            <div className={css.fieldFull}>
              <label className={css.label}>
                Import / Export Experience <span style={{ color: '#94a3b8', fontWeight: 400 }}>(Optional)</span>
              </label>
              <Field name="importExportDetails">
                {({ input }) => (
                  <textarea
                    {...input}
                    rows={3}
                    className={css.textarea}
                    placeholder="Describe your import/export experience, licences, or any relevant details…"
                  />
                )}
              </Field>
            </div>
          </section>

          <div className={css.infoBox}>
            <h4>Next Step:</h4>
            <p>Add your contact person details and set up your account credentials.</p>
          </div>

          <div className={css.buttonContainer}>
            <button type="submit" className={css.button}>
              Next : Contact Details →
            </button>
          </div>

        </form>
      )}
    />
  );
};

BuyerStep1Business.defaultProps = { initialValues: {} };
BuyerStep1Business.propTypes = {
  nextStep: PropTypes.func.isRequired,
  initialValues: PropTypes.object,
};

export default BuyerStep1Business;
